import React from "react";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { track } from "@vercel/analytics";
import styles from "./Home.module.css";

const CAL_BOOKING_URL = "https://cal.com/triggerflow/30min";

// Populate this array to activate the testimonials section.
// Shape: [{ quote: string, name: string, role: string }]
const testimonials = [];

// ── Inline SVG icons (stroke style, consistent weight) ──────────────────────

function IconWeb() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}

function IconAuto() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12" />
    </svg>
  );
}

function IconOrder() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 12h6M9 16h4" />
    </svg>
  );
}

function IconTest() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

function IconDash() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function IconSupport() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
    </svg>
  );
}

function IconConsult() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
      <path d="M9 18h6M10 22h4" />
    </svg>
  );
}

// ── Data ─────────────────────────────────────────────────────────────────────

// Core services shown prominently in the homepage grid (small-business audience).
// UI/API Test Automation appears separately below, labelled as a specialist service.
const services = [
  {
    Icon: IconWeb,
    title: "Website Builds",
    desc: "Clean, professional websites that give your business a clear online presence and make it easy for customers to enquire.",
  },
  {
    Icon: IconAuto,
    title: "Workflow & Business Automation",
    desc: "Reduce repetitive admin and connect the steps that slow your business down with practical automations and digital workflows.",
  },
  {
    Icon: IconDash,
    title: "Forms, Dashboards & Internal Tools",
    desc: "Capture the right information and give your team better visibility with custom forms, dashboards and internal tools.",
  },
  {
    Icon: IconOrder,
    title: "Ordering & Booking Systems",
    desc: "Simple digital systems for businesses that need more than a basic contact form — ordering, booking or enquiry flows built around your process.",
  },
  {
    Icon: IconConsult,
    title: "Consultancy & Digital Setup Guidance",
    desc: "Not sure what to build or which tools to use? We map out your options and give a clear recommendation before you commit to anything.",
  },
  {
    Icon: IconSupport,
    title: "Support & Maintenance",
    desc: "Ongoing updates, fixes and support once a website or system has gone live. You will not be left managing something that quietly breaks six months after handover.",
  },
];

const processSteps = [
  { num: "01", label: "Discovery", desc: "We understand your goals, pain points, and existing setup." },
  { num: "02", label: "Design",    desc: "Flows and requirements agreed before any build work starts." },
  { num: "03", label: "Build",     desc: "Work delivered in clear, manageable stages." },
  { num: "04", label: "Review",    desc: "Quality checks and walkthroughs at each stage." },
  { num: "05", label: "Launch",    desc: "Smooth handover with minimal disruption to your business." },
  { num: "06", label: "Improve",   desc: "Ongoing iteration based on real usage and feedback." },
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function Home() {
  return (
    <>
      <Helmet>
        <title>TriggerFlow — Websites, Automation &amp; Digital Tools for Small Businesses</title>
        <meta name="description" content="TriggerFlow builds websites, workflow automations, and digital tools that help small businesses capture more enquiries, reduce manual admin, and run more smoothly." />
      </Helmet>

      {/*
        ── HERO ──────────────────────────────────────────────────────────────
        Three headline options below. Option A is active.
        To switch: uncomment your preferred option, comment out the others.
        ─────────────────────────────────────────────────────────────────── */}

      {/* ── Option A: Enquiry capture angle (ACTIVE) ── */}
      <section className="hero">
        <div className="hero__inner">
          <h1 className="hero__title">
            Stop losing enquiries to a slow website and missed messages.
          </h1>
          <p className="hero__subtitle">
            TriggerFlow helps small businesses get online properly, capture every lead, and
            replace the manual processes that slow them down — with practical digital systems
            that actually get used.
          </p>
          <div className="hero__cta">
            <NavLink to="/services" className="btn btn--primary">See What We Can Build</NavLink>
            <a href={CAL_BOOKING_URL} className="btn btn--ghost" target="_blank" rel="noopener noreferrer" onClick={() => track("booking_click", { location: "hero" })}>
              Book a Free Call {/* PLACEHOLDER: Cal.com link */}
            </a>
          </div>
        </div>
      </section>

      {/*
        ── Option B: Less admin angle ──
        <section className="hero">
          <div className="hero__inner">
            <h1 className="hero__title">
              Less time on manual admin. More time on the work that matters.
            </h1>
            <p className="hero__subtitle">
              TriggerFlow builds the digital systems growing businesses need — from a
              professional website that drives enquiries to automations that cut repetitive
              work and keep things running smoothly.
            </p>
            <div className="hero__cta">
              <NavLink to="/services" className="btn btn--primary">See What We Can Build</NavLink>
              <a href={CAL_BOOKING_URL} className="btn btn--ghost" target="_blank" rel="noopener noreferrer">
                Book a Free Call
              </a>
            </div>
          </div>
        </section>

        ── Option C: Clear digital presence angle ──
        <section className="hero">
          <div className="hero__inner">
            <h1 className="hero__title">
              Give your business a clear digital presence — and the systems to back it up.
            </h1>
            <p className="hero__subtitle">
              From websites and contact forms to workflow automations and order management,
              TriggerFlow builds practical digital systems for small businesses that want to
              grow without the overhead.
            </p>
            <div className="hero__cta">
              <NavLink to="/services" className="btn btn--primary">See What We Can Build</NavLink>
              <a href={CAL_BOOKING_URL} className="btn btn--ghost" target="_blank" rel="noopener noreferrer">
                Book a Free Call
              </a>
            </div>
          </div>
        </section>
      */}

      {/* PROBLEM */}
      <section className="section section--muted">
        <div className={styles.problemInner}>
          <h2 className="section__title">Does this sound familiar?</h2>
          <ul className="bullets">
            <li>Enquiries come in from different places and some get missed.</li>
            <li>Orders, leads or tasks are tracked manually and mistakes creep in.</li>
            <li>Your team spends time copying information between disconnected systems.</li>
            <li>Your website or forms do not capture the right information clearly.</li>
            <li>You know the process could run better, but you need help mapping it out.</li>
          </ul>
          <p className={styles.problemClose}>
            TriggerFlow helps turn messy processes into clearer digital workflows.
          </p>
        </div>
      </section>

      {/* SERVICES — core small-business services */}
      <section className="section">
        <div className={styles.sectionIntro}>
          <h2 className="section__title">Digital systems we can build for your business</h2>
          <p className={styles.sectionSub}>
            From websites and contact forms to workflow automations, dashboards and ordering
            systems — each service is designed around a practical business outcome.
          </p>
        </div>
        <div className={styles.serviceGrid}>
          {services.map(({ Icon, title, desc, wide }) => (
            <article
              key={title}
              className={`card ${styles.serviceCard}${wide ? ` ${styles.serviceCardWide}` : ""}`}
            >
              <div className={styles.serviceIcon}><Icon /></div>
              {wide ? (
                <div className={styles.serviceWideText}>
                  <h3 className={styles.serviceTitle}>{title}</h3>
                  <p className={styles.serviceDesc}>{desc}</p>
                </div>
              ) : (
                <>
                  <h3 className={styles.serviceTitle}>{title}</h3>
                  <p className={styles.serviceDesc}>{desc}</p>
                </>
              )}
            </article>
          ))}
        </div>
        <div className={styles.gridCta}>
          <NavLink to="/services" className="btn btn--primary">View All Services</NavLink>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="section">
        <div className={styles.processInner}>
          <h2 className="section__title">How We Work</h2>
          <ol className={styles.processList}>
            {processSteps.map(({ num, label, desc }) => (
              <li key={label} className={styles.processStep}>
                <span className={styles.processNum}>{num}</span>
                <strong className={styles.processLabel}>{label}</strong>
                <p className={styles.processDesc}>{desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* WHY TRIGGERFLOW */}
      <section className="section section--muted">
        <div className={styles.problemInner}>
          <h2 className="section__title">Why TriggerFlow</h2>
          <ul className="bullets">
            <li><strong>Outcomes first.</strong> Every project starts with understanding what you are trying to achieve — not which technology to use.</li>
            <li><strong>Practical approach.</strong> We recommend the right tools and setup for your budget and goals — not the most complex solution.</li>
            <li><strong>Right-sized for your business.</strong> We work at a scale that suits growing teams — no enterprise overhead, no unnecessary complexity.</li>
            <li><strong>Honest pricing.</strong> Fixed quotes, no hidden retainers, no surprise invoices.</li>
            <li><strong>We stay involved.</strong> Post-launch support means problems get fixed, not ignored.</li>
          </ul>
        </div>
      </section>

      {/* HOW WE PRICE */}
      <section className="section">
        <div className={styles.pricingInner}>
          <h2 className="section__title">How we price</h2>
          <p className={styles.pricingLead}>
            Every project starts with a free scoping call. Once we understand what you need,
            we give you a fixed quote — no hourly rates, no hidden retainers, no surprise
            invoices. You know the full cost before any work begins.
          </p>
          <div className={styles.pricingPoints}>
            <div className={styles.pricingPoint}>
              <strong>Fixed price, agreed upfront.</strong>
              You know exactly what you are paying before any work starts.
            </div>
            <div className={styles.pricingPoint}>
              <strong>No retainers.</strong>
              You are not locked into ongoing fees unless you choose a support arrangement.
            </div>
            <div className={styles.pricingPoint}>
              <strong>Free scoping call first.</strong>
              We scope the work properly before quoting — so the price reflects what you actually need.
            </div>
          </div>
          <a
            href={CAL_BOOKING_URL}
            className="btn btn--primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Book a free scoping call {/* PLACEHOLDER: Cal.com link */}
          </a>
        </div>
      </section>

      {/* SPECIALIST SERVICES — UI/API Test Automation (for technical teams) */}
      <section className="section section--muted">
        <div className={styles.specialistInner}>
          <p className={styles.specialistBadge}>Specialist services</p>
          <h2 className="section__title">Technical delivery and QA support</h2>
          <p className={styles.specialistLead}>
            For software teams and technical stakeholders that need specialist test
            automation and quality assurance support alongside their existing delivery process.
          </p>
          <div className={styles.specialistCard}>
            <div className={styles.specialistIcon}><IconTest /></div>
            <div className={styles.specialistBody}>
              <h3 className={styles.specialistTitle}>UI/API Test Automation</h3>
              <p className={styles.specialistDesc}>
                We design and build automated test suites that run against your UI and
                APIs — covering the critical paths that matter most. We also offer
                standalone QA audits and test strategy reviews for teams that need a
                clearer testing approach without a full implementation.
              </p>
            </div>
            <NavLink to="/services" className={`btn btn--ghost ${styles.specialistCta}`}>
              See full service details
            </NavLink>
          </div>
        </div>
      </section>

      {/* PARTNERSHIPS — for founders with an idea */}
      <section className="section">
        <div className={styles.partnerInner}>
          <p className={styles.partnerBadge}>Partnerships</p>
          <h2 className="section__title">Have an idea but need the technical side handled?</h2>
          <p className={styles.partnerLead}>
            It starts with a conversation — no commitments, no obligation on a first call.
            Bring the idea and we will tell you honestly whether there is a fit and what
            a sensible next step looks like.
          </p>
          <p className={styles.partnerSub}>
            TriggerFlow takes on a small number of partnership projects each year. If the
            idea is commercially viable, we are open to structured arrangements — equity,
            revenue share, or phased delivery — with a clear written agreement before any
            work starts.
          </p>
          <div className={styles.partnerPoints}>
            <div className={styles.partnerPoint}>
              <strong>Starts with a conversation.</strong> No commitments on a first call.
              An idea review and written agreement come before any build starts.
            </div>
            <div className={styles.partnerPoint}>
              <strong>Selective by design.</strong> The idea needs to be commercially viable,
              clearly scoped, and a good fit for our technical strengths.
            </div>
            <div className={styles.partnerPoint}>
              <strong>Structured, not open-ended.</strong> Every arrangement is properly agreed
              with clear expectations on both sides — not open-ended or unpaid.
            </div>
          </div>
          <NavLink to="/contact" className="btn btn--primary">Start a Conversation</NavLink>
        </div>
      </section>

      {/* TESTIMONIALS — only renders when the array above is populated */}
      {testimonials.length > 0 && (
        <section className="section section--muted">
          <div className={styles.testimonialsInner}>
            <h2 className="section__title">What clients say</h2>
            <div className={styles.testimonialGrid}>
              {testimonials.map(({ quote, name, role }) => (
                <blockquote key={name} className={styles.testimonial}>
                  <p className={styles.testimonialQuote}>{quote}</p>
                  <footer className={styles.testimonialAttrib}>
                    <strong>{name}</strong>
                    {role && <span>, {role}</span>}
                  </footer>
                </blockquote>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className={`section ${styles.ctaSection}`}>
        <div className="cta">
          <h2>Ready to improve the way your business works?</h2>
          <p>
            Book a free 30-minute call. We will look at your current setup and
            suggest what would make the most practical difference.
          </p>
          <div className={styles.ctaButtons}>
            <a
              href={CAL_BOOKING_URL}
              className="btn btn--primary"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("booking_click", { location: "final_cta" })}
            >
              Book a Free Call {/* PLACEHOLDER: Cal.com link */}
            </a>
            <NavLink to="/contact" className="btn btn--ghost">Send a Message</NavLink>
          </div>
        </div>
      </section>
    </>
  );
}
